<template>
  <configuration-menu-name title="Inhalt" :menu="menu" max="255" @update:data="$emit('update:menu', $event)">
    <p>
      Befüllen Sie nun die Seiten Ihrer Gastrokarte!<br />
      Gliedern Sie sie nach Ihren Wünschen in Speisen und Getränke, fügen Sie Angaben zu Mengen, Preisen und Allergenen
      hinzu.
    </p>
  </configuration-menu-name>
  <div
    v-for="error in errors"
    :key="error"
    role="alert"
    class="alert mb-3 alert-primary border-primary shadow"
    v-html="error"
  ></div>
  <div v-if="categories.length > 0" class="d-flex justify-content-end pe-3" style="margin-top: -1rem">
    <div class="checkbox">
      <label class="d-flex gap-2 align-items-center mb-1">
        Mehrfachauswahl
        <input v-model="multicheck" type="checkbox" class="form-check-input" />
        <div class="check fs-4"></div>
      </label>
    </div>
  </div>
  <draggable
    :list="categories"
    handle=".handle"
    item-key="id"
    group="categories"
    ghost-class="ghost"
    @change="changeSort"
  >
    <template #item="{ element: category, index: catIndex }">
      <div class="card shadow rounded-corner mb-3">
        <div v-if="category.attributes?.category_type == 'freetext'" class="card-body">
          <div class="d-flex align-items-center justify-content-between">
            <h5
              class="d-flex text-secondary fw-bold mb-0 handle justify-content-between flex-row align-items-center"
              :class="{ disabled: multicheck }"
            >
              <div>
                <svg
                  height="1em"
                  viewBox="0 0 1792 1792"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  class="cursor-move"
                  style="margin-top: -0.25rem"
                >
                  <path
                    d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
                  ></path>
                </svg>
                <span class="ms-2" style="color: #a05a60">
                  <base-icon-freetext></base-icon-freetext>
                </span>
                {{ category.attributes.title ?? 'freie Texteingabe' }}
              </div>
            </h5>
            <div v-if="!multicheck" class="btn-group ms-3" role="group">
              <button
                v-if="editor == 'cat-' + category.id"
                type="button"
                class="btn border text-muted hover:text-secondary"
                @click="updateFreetextData(category)"
              >
                <base-icon-save></base-icon-save>
              </button>
              <button
                v-if="editor == 'cat-' + category.id"
                type="button"
                class="btn border text-muted hover:text-primary"
                @click="confirmAndDeleteCategory(category.id, 'Kategorie „freie Texteingabe“ löschen?', 'Möchten Sie die Kategorie „freie Texteingabe“ wirklich löschen?')"
              >
                <i class="bi bi-trash-fill"></i>
              </button>
              <button
                v-else-if="editor != 'cat-' + category.id"
                type="button"
                class="btn border text-muted hover:text-secondary"
                @click="changeEditor('cat-' + category.id)"
              >
                <i class="bi bi-pencil-fill"></i>
              </button>
            </div>
            <div v-if="multicheck" class="checkbox ms-3">
              <label class="d-flex gap-2 align-items-center mb-1">
                <input
                  type="checkbox"
                  class="form-check-input"
                  :checked="multichecked.indexOf('cat-' + category.id) != -1"
                  @change="
                    updateMultichecked(
                      $event.target.checked
                        ? union(multichecked, ['cat-' + category.id])
                        : difference(multichecked, ['cat-' + category.id])
                    )
                  "
                />
                <div class="check fs-4"></div>
              </label>
            </div>
          </div>
          <div v-if="editor == 'cat-' + category.id">
            <div class="editor">
              <base-form-input
                :name="editor + '-title'"
                :label="'Optionale Kategoriebeschreibung, z. B. „Alkoholfreie Getränke“ (nicht sichtbar auf der gedruckten Karte)'"
                :value="category.attributes.title"
                :errors="category.errors ? category.errors.title : null"
                @update:data="updateDataWithErrorCheck"
              ></base-form-input>

              <quill-editor
                v-model:content="contents[category.id]"
                :toolbar="'#toolbar-' + category.id"
                theme="snow"
                @ready="editorReady($event, category.id)"
              >
                <template #toolbar>
                  <div :id="'toolbar-' + category.id">
                    <select class="ql-size">
                      <option selected value="">Normal</option>
                      <option value="small">Klein</option>
                    </select>
                    <button class="ql-bold"></button>
                    <button class="ql-italic"></button>
                    <button class="ql-underline"></button>
                    <button class="ql-header" value="1">Überschrift</button>
                  </div>
                </template>
              </quill-editor>
            </div>
          </div>
        </div>

        <div
          v-else-if="category.attributes?.category_type == 'pagebreak'"
          class="card-body d-flex justify-content-between align-items-center"
        >
          <h5 class="text-secondary fw-bold mb-0 handle" :class="{ disabled: multicheck }">
            <svg
              height="1em"
              viewBox="0 0 1792 1792"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              class="cursor-move"
              style="margin-top: -0.25rem"
            >
              <path
                d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
              ></path>
            </svg>
            <span class="ms-2" style="color: #a05a60">
              <base-icon-pagebreak></base-icon-pagebreak>
            </span>
            Seitenumbruch
          </h5>
          <div v-if="multicheck" class="checkbox ms-3">
            <label class="d-flex gap-2 align-items-center mb-1">
              <input
                type="checkbox"
                class="form-check-input"
                :checked="multichecked.indexOf('cat-' + category.id) != -1"
                @change="
                  updateMultichecked(
                    $event.target.checked
                      ? union(multichecked, ['cat-' + category.id])
                      : difference(multichecked, ['cat-' + category.id])
                  )
                "
              />
              <div class="check fs-4"></div>
            </label>
          </div>
          <button
            v-else
            type="button"
            class="btn border text-muted hover:text-primary"
            @click="confirmAndDeleteCategory(category.id, 'Seitenumbruch löschen?', 'Möchten Sie diesen Seitenumbruch wirklich löschen?')"
          >
            <i class="bi bi-trash-fill"></i>
          </button>
        </div>
        <configuration-category-combo
          v-else-if="category.attributes?.category_type == 'combo'"
          :category="category"
          :editor="editor"
          :change-editor="changeEditor"
          :delete-category="(id) => confirmAndDeleteCategory(id, 'Kombo-Angebot löschen?', 'Möchten Sie dieses Kombo-Angebot wirklich löschen?')"
          :multicheck="multicheck"
          :multichecked="multichecked"
          :combos="categoryMeta.combos"
          @update:data="updateDataWithErrorCheck($event)"
          @update:multichecked="updateMultichecked"
        ></configuration-category-combo>
        <configuration-category-filler
          v-else-if="category.attributes?.category_type == 'filler'"
          :category="category"
          :editor="editor"
          :change-editor="changeEditor"
          :delete-category="(id) => confirmAndDeleteCategory(id, 'Designseite löschen?', 'Möchten Sie diesen Designseite wirklich löschen?')"
          :multicheck="multicheck"
          :multichecked="multichecked"
          @update:data="updateDataWithErrorCheck($event)"
          @update:multichecked="updateMultichecked"
        ></configuration-category-filler>
        <div v-else class="card-body pt-0">
          <div
            :ref="
              (el) => {
                catHeaders[catIndex] = el;
              }
            "
            :data-category="category.id"
            :class="{
              sticky: category.sticky && categoryIsOpen(category.id),
            }"
            class="bg-white pt-3 d-flex contentCatHeader"
            :style="{ top: headerCurveBottom + 'px' }"
          >
            <div class="w-100" :class="!categoryIsOpen(category.id) && 'd-flex align-items-center gap-1'">
              <div v-if="editor == 'cat-' + category.id" class="mb-3">
                <base-form-input
                  :name="editor + '-title'"
                  :label="
                    'Fügen Sie hier die Überschrift Ihrer Kategorie ein z. B. ' +
                    (category.attributes?.category_type == 'food' ? 'Vorspeisen' : 'Säfte')
                  "
                  :value="category.attributes.title"
                  :errors="category.errors ? category.errors.title : null"
                  @update:data="updateDataWithErrorCheck"
                ></base-form-input>
                <base-textarea
                  :name="editor + '-subtitle'"
                  :label="
                    'Fügen Sie hier optional eine Beschreibung der Kategorie hinzu z. B. ' +
                    (category.attributes?.category_type == 'food'
                      ? 'Kalte und warme Gerichte zum Teilen oder alleine Genießen'
                      : 'Alle Getränke werden mit Eiswürfeln serviert')
                  "
                  :max="255"
                  :value="category.attributes.subtitle || ''"
                  :errors="category.errors ? category.errors.subtitle : null"
                  @update:data="updateDataWithErrorCheck"
                />
              </div>
              <template v-else>
                <template v-if="category.attributes">
                  <h5 class="d-inline-block text-secondary fw-bold mb-0 handle" :class="{ disabled: multicheck }">
                    <svg
                      height="1em"
                      viewBox="0 0 1792 1792"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      class="cursor-move"
                      style="margin-top: -0.25rem"
                    >
                      <path
                        d="M1408 1088q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45zm0-384q0 26-19 45t-45 19h-896q-26 0-45-19t-19-45 19-45l448-448q19-19 45-19t45 19l448 448q19 19 19 45z"
                      ></path>
                    </svg>
                    {{ category.attributes.title }}
                  </h5>
                  <template v-if="categoryIsOpen(category.id)">
                    <p v-if="category.attributes.subtitle">
                      {{ category.attributes.subtitle }}
                    </p>
                  </template>
                  <template v-else>
                    ({{ articlesByCategory(category.id).length }}
                    Artikel)
                  </template>
                </template>
              </template>
            </div>
            <div>
              <div
                v-if="multicheck"
                class="checkbox ms-3"
                :class="{
                  'mb-3': category.relationships.menuItems.data.length > 0,
                }"
              >
                <label class="d-flex gap-2 align-items-center mb-1">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="multichecked.indexOf('cat-' + category.id) != -1"
                    @change="
                      updateMultichecked(
                        $event.target.checked
                          ? union(multichecked, ['cat-' + category.id])
                          : difference(multichecked, ['cat-' + category.id])
                      )
                    "
                  />
                  <div class="check fs-4"></div>
                </label>
              </div>
              <div
                v-else-if="editor != 'cat-' + category.id"
                class="btn-group ms-3"
                :class="categoryIsOpen(category.id) && !category.attributes.subtitle && 'mb-3'"
                role="group"
              >
                <button
                  type="button"
                  class="btn border text-muted hover:text-secondary"
                  @click="toggleCategory(category.id)"
                >
                  <i
                    class="bi"
                    :class="openCategories.indexOf(category.id) >= 0 ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"
                  ></i>
                </button>
                <button
                  type="button"
                  class="btn border text-muted hover:text-secondary"
                  @click="changeEditor('cat-' + category.id)"
                >
                  <i class="bi bi-pencil-fill"></i>
                </button>
              </div>
              <div v-else class="btn-group-vertical ms-3" role="group">
                <button
                  type="button"
                  class="btn border text-muted hover:text-secondary"
                  @click="changeEditor('cat-' + category.id)"
                >
                  <base-icon-save></base-icon-save>
                </button>
                <button
                  type="button"
                  class="btn border text-muted hover:text-primary"
                  @click="confirmAndDeleteCategory(category.id, 'Kategorie löschen?', 'Möchten Sie die Kategorie ' + (category.attributes.title || 'Unbenannt') + ' und alle darin enthaltenen Artikel wirklich löschen?')"
                >
                  <i class="bi bi-trash-fill"></i>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="
              categoryIsOpen(category.id) &&
              (!multicheck || (multicheck && category.relationships.menuItems.data.length > 0))
            "
            class="border-secondary border rounded px-3 pb-0 pt-2 text-center"
          >
            <div v-if="multicheck" class="d-flex justify-content-end border-bottom mb-2 checkbox">
              <label class="d-flex gap-2 align-items-center mb-2">
                {{ category.attributes.title ? 'alles in ' + category.attributes.title : 'alles auswählen' }}
                <input
                  v-model="multicheckedAllins"
                  type="checkbox"
                  class="form-check-input"
                  :value="'allin-' + category.id"
                  @change="multicheckAllin(category.id)"
                />
                <div class="check fs-4"></div>
              </label>
            </div>
            <draggable
              :list="category.relationships.menuItems.data"
              ghost-class="ghost"
              group="articles"
              handle=".handle"
              item-key="id"
              @change="changeSort"
            >
              <template #item="{ element: article }">
                <configuration-menu-item
                  :article="articleById(article.id)"
                  :category-type="category.attributes?.category_type"
                  :ingredients="ingredients"
                  :menu-id="Number(menu.id)"
                  :editor="editor"
                  :change-editor="changeEditor"
                  :delete-article="(id) => confirmAndDeleteArticle(id)"
                  :multicheck="multicheck"
                  :multichecked="multichecked"
                  @update:data="updateDataWithErrorCheck"
                  @update:tags="updateItemTags"
                  @update:ingredients="updateItemIngredients"
                  @update:multichecked="updateMultichecked"
                >
                </configuration-menu-item>
              </template>
            </draggable>
            <div v-if="!multicheck" role="group" class="btn-group dropup position-sticky m-3 mt-2" style="bottom: 1rem">
              <button
                class="btn btn-secondary text-white rounded-pill ps-5"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i class="bi bi-plus-circle-fill position-absolute h4 lh-1 start-0 ms-2"></i>
                Artikel
                <span v-if="category.attributes && category.attributes.title"
                  >zu "{{ category.attributes.title }}" </span
                >hinzufügen
              </button>
              <ul class="dropdown-menu" aria-labelledby="addItemDropdown">
                <li>
                  <span class="dropdown-item cursor-pointer" @click="addArticle('-default', category.id)">
                    <i class="bi bi-plus-circle-fill"></i>
                    Artikel
                  </span>
                </li>
                <li>
                  <span class="dropdown-item cursor-pointer" @click="addArticle('-blankline', category.id)">
                    <base-icon-blankline></base-icon-blankline>
                    Leerzeile
                  </span>
                </li>
                <li>
                  <span class="dropdown-item cursor-pointer" @click="addArticle('-pagebreak', category.id)">
                    <base-icon-pagebreak></base-icon-pagebreak>
                    Seitenumbruch
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </draggable>
  <div v-if="categories.length <= 0" class="bg-white border shadow p-3 pb-5">
    <p>Sie haben noch keine Kategorien. Fügen sie als Erstes eine Kategorie hinzu.</p>
  </div>
  <div
    :style="{
      bottom: (categories.length <= 0 ? 3.5 : 1) + 'rem',
      width: !multicheck ? '16rem' : 'auto',
      marginLeft: 'auto',
    }"
    class="d-flex"
    :class="
      categories.length <= 0 ? 'position-relative justify-content-start ms-3' : 'position-sticky justify-content-end'
    "
  >
    <div v-if="multicheck">
      <label class="btn bg-muted hover:bg-muted border rounded-pill me-3">
        abbrechen
        <input v-model="multicheck" class="d-none" type="checkbox" />
      </label>
      <button class="btn btn-danger rounded-pill ps-5 position-relative" @click="deleteMultichecked">
        ausgewählte löschen
        <i
          class="bi bi-trash-fill bg-white text-danger rounded-circle position-absolute start-0 ms-2 p-1 d-flex align-items-center justify-content-center"
          style="width: 1.5rem; height: 1.5rem"
        ></i>
      </button>
    </div>
    <configuration-add-category-button
      v-else
      :add-category="addCategoryWithUpdateData"
      :menu-id="Number(menu.id)"
      :menu="menu"
    ></configuration-add-category-button>
  </div>

  <Teleport to="body">
    <div
        v-if="isModalOpen"
        class="modal fade show"
        tabindex="-1"
        aria-modal="true"
        role="dialog"
        style="display: block; background-color: rgba(0, 0, 0, 0.5);"
        @click.self="cancelAction"
    >
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content shadow-lg">
                <div class="modal-body p-4 p-sm-5">
                    <div class="d-sm-flex align-items-start">
                        <div class="mx-auto flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-danger-subtle mb-3 mb-sm-0" style="width: 40px; height: 40px;">
                             <svg class="text-danger" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                            </svg>
                        </div>
                        <div class="mt-3 mt-sm-0 ms-sm-4 text-center text-sm-start w-100">
                            <h5 class="modal-title mb-2" id="modal-title">{{ modalProps.title }}</h5>
                            <div>
                                <p class="text-muted">{{ modalProps.message }}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer flex-column flex-sm-row-reverse">
                    <button
                        type="button"
                        class="btn w-100 w-sm-auto"
                        style="text-decoration: underline"
                        @click="cancelAction"
                        ref="cancelButtonRef"
                    >
                         {{ modalProps.cancelText }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger w-100 mb-2 mb-sm-0 w-sm-auto"
                        @click="confirmAction"
                    >
                        {{ modalProps.confirmText }}
                    </button>
                </div>
            </div>
        </div>
    </div>
  </Teleport>
</template>

<script>
import { onMounted, onUnmounted, onBeforeUpdate, ref, reactive, readonly, watch } from 'vue';
import { Teleport } from 'vue';

import draggable from 'vuedraggable';

import useIngredients from '@/composables/useIngredients';
import useContent from '@/composables/useContent';
import useMenus from '@/composables/useMenus';

import difference from 'lodash/difference';
import union from 'lodash/union';

import ConfigurationMenuItem from '@/components/ConfigurationMenuItem';
import ConfigurationMenuName from '@/components/ConfigurationMenuName';
import ConfigurationAddCategoryButton from '@/components/ConfigurationAddCategoryButton';
import ConfigurationCategoryCombo from '@/components/ConfigurationCategoryCombo';
import ConfigurationCategoryFiller from '@/components/ConfigurationCategoryFiller';

import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'ConfigurationMenuContent',
  components: {
    draggable,
    ConfigurationMenuItem,
    ConfigurationMenuName,
    ConfigurationAddCategoryButton,
    ConfigurationCategoryCombo,
    ConfigurationCategoryFiller,
    QuillEditor,
    Teleport,
  },
  props: {
    menu: { type: Object, default: null },
  },
  emits: ['menuDataChanged', 'update:menu'],
  setup(props, { emit }) {
    const {
      addArticle,
      addCategory,
      articleById,
      articlesByCategory,
      categories,
      categoryMeta,
      categoryIsOpen,
      closeAllCategories,
      changeEditor,
      deleteArticle: originalDeleteArticle,
      deleteCategory: originalDeleteCategory,
      editor,
      fetchMenuCategories,
      openAllCategories,
      openCategories,
      toggleCategory,
      updateData,
      updateItemIngredients,
      updateItemTags,
    } = useContent(props.menu.id);
    const { checkErrorsMenu } = useMenus();

    const errors = ref([]);

    fetchMenuCategories();

    const isModalOpen = ref(false);
    const modalProps = reactive({
        title: '',
        message: '',
        confirmText: 'Loschen',
        cancelText: 'Abbrechen',
    });
    const resolvePromise = ref(null);

    const openModal = (modalConfig) => {
        Object.assign(modalProps, {
            confirmText: 'Loschen',
            cancelText: 'Abbrechen',
            ...modalConfig
        });
        isModalOpen.value = true;
        return new Promise((resolve) => {
            resolvePromise.value = resolve;
        });
    };

    const confirmAction = () => {
        if (resolvePromise.value) {
            resolvePromise.value(true);
        }
        resetModalState();
    };

    const cancelAction = () => {
        if (resolvePromise.value) {
            resolvePromise.value(false);
        }
        resetModalState();
    };

     const resetModalState = () => {
        isModalOpen.value = false;
        modalProps.title = '';
        modalProps.message = '';
        resolvePromise.value = null;
    };


    const catHeaders = ref([]);

    const headerCurveBottom = ref(0);

    const scrollHandler = () => {
        if (!catHeaders.value || catHeaders.value.length === 0) return;
        const currentHeaderBottom = document.querySelector('header .curve')?.getBoundingClientRect().bottom + 56 || 56;
        catHeaders.value.forEach((entry, index) => {
            if (categories.value[index] && entry) {
                if (entry.getBoundingClientRect().top <= currentHeaderBottom) {
                    categories.value[index].sticky = true;
                } else {
                    categories.value[index].sticky = false;
                }
            }
      });
    };

    const changeSort = (event) => {
      if (event.moved) {
        let targetId = event.moved.element.id + '-sort_num';
        if (event.moved.element.type === 'menu-categories') {
          targetId = 'cat-' + targetId;
        } else if (event.moved.element.type === 'menu-items') {
          targetId = 'art-' + targetId;
        }
        updateData({ target: { id: targetId, value: event.moved.newIndex } });
      }
    };

    const editors = ref({});
    const editorReady = (quillInstance, categoryId) => {
      const category = categories.value.find(cat => cat.id == categoryId);
      if(category && category.attributes.data) {
          try {
             quillInstance.setContents(category.attributes.data);
          } catch (e) {
            console.error("Failed to set Quill contents for category", categoryId, e);
            quillInstance.setText('');
          }

      }
      editors.value[categoryId] = quillInstance;
    };

    const contents = ref({});
    const updateFreetextData = async (category) => {
      const contentToSave = contents.value[category.id] || '';
      await updateData({ target: { id: 'cat-' + category.id + '-data', value: contentToSave } });
      changeEditor('cat-' + category.id);
      setTimeout(() => updateErrors(), 1200);
    };

    const { ingredients, fetchIngredients } = useIngredients();
    const loadIngredients = () => {
         if (props.menu.attributes.data?.allergens?.info === undefined || props.menu.attributes.data?.allergens?.info == 1) {
            fetchIngredients();
        }
    };
    loadIngredients();

    const multicheck = ref(false);
    const multichecked = ref([]);
    const updateMultichecked = (updatedValue) => {
      multichecked.value = updatedValue;

      const categoriesToCheck = new Set();
      multichecked.value.forEach(entry => {
          const [type, id] = entry.split('-');
          if (type === 'art') {
              const article = articleById(id);
              if (article) categoriesToCheck.add(article.attributes.menu_category_id);
          } else if (type === 'cat') {
              categoriesToCheck.add(id);
          }
      });

      const newAllins = [];
      categoriesToCheck.forEach(catId => {
          const articlesInCat = articlesByCategory(catId).map(item => `art-${item.id}`);
          const selectedArticlesInCat = articlesInCat.filter(artId => multichecked.value.includes(artId));
          if (articlesInCat.length > 0 && selectedArticlesInCat.length === articlesInCat.length && multichecked.value.includes(`cat-${catId}`)) {
              newAllins.push(`allin-${catId}`);
          }
      });
      multicheckedAllins.value = newAllins;
    };

    const multicheckedAllins = ref([]);
    const multicheckAllin = (catId) => {
      const allArticlesInCategory = articlesByCategory(catId).map((item) => 'art-' + item.id);
      const catEntry = 'cat-' + catId;

      if (multicheckedAllins.value.includes('allin-' + catId)) {
        multichecked.value = union(multichecked.value, allArticlesInCategory, [catEntry]);
      } else {
        multichecked.value = difference(multichecked.value, allArticlesInCategory, [catEntry]);
      }
    };

    const confirmAndDeleteCategory = async (id, title = 'Kategorie löschen?', message = 'Möchten Sie diese Kategorie und alle zugehörigen Artikel wirklich löschen?', moreIds = []) => {
      const confirmed = await openModal({ title, message });
      if (confirmed) {
        await originalDeleteCategory(id, moreIds);
        setTimeout(() => updateErrors(), 1200);
      }
    };

    const confirmAndDeleteArticle = async (id, moreIds = []) => {
       const article = articleById(id);
       const title = 'Artikel löschen?';
       const message = `Möchten Sie den Artikel "${article?.attributes?.title || 'Unbenannt'}" wirklich löschen?`;
       const confirmed = await openModal({ title, message });
        if (confirmed) {
            await originalDeleteArticle(id, moreIds);
            setTimeout(() => updateErrors(), 1200);
        }
    };

    const confirmAndDeleteMultichecked = async () => {
      const numCats = multichecked.value.filter(e => e.startsWith('cat-')).length;
      const numArts = multichecked.value.filter(e => e.startsWith('art-')).length;

      if(numCats === 0 && numArts === 0) return;

      let message = `Möchten Sie die ausgewählten ${numCats > 0 ? numCats + ' Kategorien' : ''}${numCats > 0 && numArts > 0 ? ' und ' : ''}${numArts > 0 ? numArts + ' Artikel' : ''} wirklich löschen?`;
      if(numCats > 0) message += ' Das Löschen von Kategorien entfernt auch die darin enthaltenen Inhalte.';

      const confirmed = await openModal({
          title: 'Auswahl löschen?',
          message: message,
      });

      if (confirmed) {
        let articlesToDelete = [];
        let categoriesToDelete = [];
        multichecked.value.forEach((entry) => {
            const [type, id] = entry.split('-');
            if (type == 'art') {
            articlesToDelete.push(id);
            } else if (type == 'cat') {
            categoriesToDelete.push(id);
            }
        });

        if (articlesToDelete.length) {
            await originalDeleteArticle(articlesToDelete.pop(), articlesToDelete);
        }
        if (categoriesToDelete.length) {
            await originalDeleteCategory(categoriesToDelete.pop(), categoriesToDelete);
        }

        multichecked.value = [];
        multicheckedAllins.value = [];
        if (categories.value.length === 0) {
            multicheck.value = false;
        }
        setTimeout(() => updateErrors(), 1200);
      }
    };

    const updateDataWithErrorCheck = async (event) => {
      await updateData(event);
      setTimeout(() => updateErrors(), 1200);
    };

    const addCategoryWithUpdateData = async (type, writeMenuId) => {
      await addCategory(type, writeMenuId);
      setTimeout(() => updateErrors(), 1200);
    };

    const updateErrors = async () => {
      if (props.menu && props.menu.id) {
        errors.value = await checkErrorsMenu(props.menu.id);
      } else {
        errors.value = [];
        console.warn("Cannot check errors: menu ID is missing.");
      }
    };

    watch(
      () => multicheck.value,
      (isMulti) => {
        if (!isMulti) {
            editor.value = '';
            multichecked.value = [];
            multicheckedAllins.value = [];
            closeAllCategories();
        } else {
             openAllCategories();
        }
      }
    );

     watch(() => props.menu.id, (newMenuId, oldMenuId) => {
        if (newMenuId !== oldMenuId && newMenuId) {
            fetchMenuCategories();
            loadIngredients();
            updateErrors();
            multicheck.value = false;
            editor.value = '';
            contents.value = {};
        }
    }, { immediate: true });


    onMounted(async () => {
      const headerCurve = document.querySelector('header .curve');
      headerCurveBottom.value = headerCurve ? headerCurve.getBoundingClientRect().bottom + 56 : 56;

      window.addEventListener('scroll', scrollHandler);
      await updateErrors();
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', scrollHandler);
    });

    onBeforeUpdate(() => {
      catHeaders.value = [];
    });

    return {
      addArticle,
      addCategory,
      articleById,
      articlesByCategory,
      categories,
      categoryMeta,
      categoryIsOpen,
      changeEditor,
      originalDeleteArticle,
      originalDeleteCategory,
      editor,
      openCategories,
      toggleCategory,
      updateData,
      updateItemIngredients,
      updateItemTags,

      catHeaders,
      changeSort,
      deleteMultichecked: confirmAndDeleteMultichecked,
      difference,
      errors,
      headerCurveBottom,
      ingredients,
      multicheck,
      multicheckAllin,
      multichecked,
      multicheckedAllins,
      union,
      contents,
      editorReady,
      updateFreetextData,
      updateMultichecked,
      updateDataWithErrorCheck,
      addCategoryWithUpdateData,

      isModalOpen: readonly(isModalOpen),
      modalProps: readonly(modalProps),
      confirmAction,
      cancelAction,

      confirmAndDeleteCategory,
      confirmAndDeleteArticle,
    };
  },
};
</script>

<style>
.editor {
  padding: 10px;
  width: 100%;
  tab-size: 24vw !important;
  -moz-tab-size: 24vw !important;
  -o-tab-size: 24vw !important;
}

.ql-editor {
  tab-size: 24vw !important;
  -moz-tab-size: 24vw !important;
  -o-tab-size: 24vw !important;
  min-height: 100px;
}

@media (min-width: 2400px) {
  .editor, .ql-editor {
    tab-size: 18vw !important;
    -moz-tab-size: 18vw !important;
    -o-tab-size: 18vw !important;
  }
}

@media (min-width: 3200px) {
  .editor, .ql-editor {
    tab-size: 12vw !important;
    -moz-tab-size: 12vw !important;
    -o-tab-size: 12vw !important;
  }
}

@media (min-width: 4800px) {
  .editor, .ql-editor {
    tab-size: 10vw !important;
    -moz-tab-size: 10vw !important;
    -o-tab-size: 10vw !important;
  }
}

.tt {
  position: relative;
  display: inline-block;
  border-bottom: 1px dotted black;
}

.tt .tttext {
  visibility: hidden;
  width: 300px;
  background-color: #555;
  color: #fff;
  text-align: center;
  padding: 5px 0;
  border-radius: 6px;

  position: absolute;
  z-index: 1030;
  bottom: 125%;
  left: 50%;
  margin-left: -150px;

  opacity: 0;
  transition: opacity 0.3s;
}

.tt .tttext::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #555 transparent transparent transparent;
}

.tt:hover .tttext {
  visibility: visible;
  opacity: 1;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.cursor-move {
    cursor: move;
}

.disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.contentCatHeader.sticky {
    position: sticky;
    z-index: 10;
    border-bottom: 1px solid #dee2e6;
}


.modal-backdrop.fade {
    opacity: .5;
}
.modal-backdrop.show {
    opacity: .5;
}

.modal.fade.show {
    z-index: 1055;
}

.handle.disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.modal-footer .btn {
    margin-left: 0.25rem;
    margin-right: 0.25rem;
}
@media (max-width: 575.98px) {
    .modal-footer {
        justify-content: stretch;
    }
    .modal-footer .btn:not(:last-child) {
        margin-bottom: 0.5rem;
    }
}

.ql-header {
  white-space: normal !important;
  word-wrap: break-word !important;
  width: auto !important;
  padding: 0 !important;
  font-size: 14px !important;
}
</style>
